import {
    SpacingToken,
    UiStack,
} from '@experiences/ui-common';
import { useNavigateWithParams } from '@experiences/util';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import {
    ApButton,
    ApFilePicker,
    ApTextField,
} from '@uipath/portal-shell-react';
import React from 'react';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useBreadcrumbs } from '../../common/providers/BreadcrumbProvider';
import UiForm from '../common/UiForm';
import UiPageContainer from '../common/UiPageContainer/UiPageContainer';
import AdminBreadCrumbs from '../organizationsettings/AdminBreadCrumbs';
import useRegisterEdgeViewModel from './RegisterEdgeViewModel';

const useStyles = makeStyles((theme) =>
    createStyles({
        input: { marginBottom: '26px' },
        inputFile: { marginTop: 20 },
        errorMessage: {
            color: 'red',
            fontSize: '12px',
            marginTop: '4px',
        },
    }),
);

const RegisterEdgeComponent: React.FC = () => {
    const { formatMessage: translate } = useIntl();
    const { breadcrumbs } = useBreadcrumbs();
    const navigate = useNavigateWithParams();
    const classes = useStyles();

    const {
        methods: {
            formState: {
                errors, isSubmitting, isDirty,
            },
            handleSubmit,
            setValue,
            clearErrors,
            control,
        },
    } = useRegisterEdgeViewModel();

    return (
        <UiPageContainer
            breadcrumb={<AdminBreadCrumbs breadCrumbTrail={breadcrumbs} />}
            maxWidth='900px'>
            <UiForm
                onSubmit={handleSubmit}
                actions={
                    <UiStack
                        direction='row'
                        justify='end'
                        gap={SpacingToken.S}>
                        <ApButton
                            onClick={() => navigate(-1)}
                            variant="secondary"
                            data-cy="add-automation-edge-cancel"
                            label={translate({ id: 'CLIENT_CANCEL' })}
                        />
                        <ApButton
                            type="submit"
                            loading={isSubmitting}
                            variant="primary"
                            data-cy="tenant-tag-label-add-edit-button"
                            label={translate({ id: 'CLIENT_ADD' })}
                            disabled={!isDirty || Object.keys(errors).length > 0}
                        />
                    </UiStack>
                }
                centerChild
                dataCy="register-edge-component"
            >
                <Controller
                    name="customDomain"
                    control={control}
                    rules={{
                        required: translate(
                            { id: 'CLIENT_REQUIRED_FIELD_ERROR_SPECIFIC' },
                            { 0: translate({ id: 'CLIENT_EDGE_FQDN' }) },
                        ),
                        validate: {
                            notEmpty: v => !!v?.trim(),
                            validUrl: v => {
                                try {
                                    v = 'https://' + v;
                                    new URL(v);
                                    return true;
                                } catch {
                                    return translate({ id: 'CLIENT_EDGE_INVALID_FQDN' });
                                }
                            },
                        },
                    }}
                    render={({ field }) => (
                        <ApTextField
                            required
                            label={translate({ id: 'CLIENT_EDGE_FQDN' })}
                            placeholder={translate({ id: 'CLIENT_EDGE_ADD_FQDN' })}
                            error={!!errors.customDomain}
                            helperText={errors?.customDomain?.message}
                            onValueChanged={e => field.onChange(e.detail)}
                            data-cy="edge-fqdn"
                            {...field}
                        />
                    )}
                />
                <Controller
                    name="cidr"
                    control={control}
                    render={({ field }) => (
                        <ApTextField
                            label={translate({ id: 'CLIENT_EDGE_ALLOWED_IPS' })}
                            placeholder={translate({ id: 'CLIENT_EDGE_ADD_ALLOWED_IPS' })}
                            error={!!errors.cidr}
                            helperText={errors?.cidr?.message}
                            onValueChanged={e => field.onChange(e.detail)}
                            data-cy="edge-allowed-ips"
                            {...field}
                        />
                    )}
                />
                <div className={classes.inputFile}>
                    <ApFilePicker
                        label={translate({ id: 'CLIENT_UPLOAD_KUBECONFIG' })}
                        data-cy="file-input"
                        dragAndDrop
                        multiple={false}
                        errorMessage={errors?.kubeconfigFile?.message}
                        onFilesSelected={(event) => {
                            const files = event.detail;
                            if (files && files.length > 0) {
                                clearErrors('kubeconfigFile');
                                setValue('kubeconfigFile', files[0], { shouldDirty: true });
                            }
                        }}
                    />
                </div>
            </UiForm>

        </UiPageContainer>
    );
};

export default RegisterEdgeComponent;
